import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Bell, BellOff, Globe, Plus, Clock, Mail, Check, X, 
  ExternalLink, Loader2, Send, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

export default function SettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Notification preferences
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifFrequency, setNotifFrequency] = useState("daily");
  const [notifFields, setNotifFields] = useState([]);
  const [notifCountries, setNotifCountries] = useState([]);
  const [notifKeywords, setNotifKeywords] = useState("");
  
  // Website submission
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteName, setWebsiteName] = useState("");
  const [websiteDescription, setWebsiteDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [approvedSources, setApprovedSources] = useState([]);
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({ fields: [], countries: [] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prefsRes, filtersRes, submissionsRes, sourcesRes] = await Promise.all([
          fetch(`${API}/notifications/preferences`, { credentials: "include" }),
          fetch(`${API}/jobs/filters/options`),
          fetch(`${API}/websites/submissions`, { credentials: "include" }),
          fetch(`${API}/websites/approved`)
        ]);

        if (prefsRes.ok) {
          const prefs = await prefsRes.json();
          setNotifEnabled(prefs.enabled || false);
          setNotifFrequency(prefs.frequency || "daily");
          setNotifFields(prefs.fields || []);
          setNotifCountries(prefs.countries || []);
          setNotifKeywords((prefs.keywords || []).join(", "));
        }

        if (filtersRes.ok) {
          const filters = await filtersRes.json();
          setFilterOptions(filters);
        }

        if (submissionsRes.ok) {
          const subs = await submissionsRes.json();
          setUserSubmissions(subs);
        }

        if (sourcesRes.ok) {
          const sources = await sourcesRes.json();
          setApprovedSources(sources);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const saveNotificationPreferences = async () => {
    setSaving(true);
    try {
      const keywords = notifKeywords.split(",").map(k => k.trim()).filter(k => k);
      
      const response = await fetch(`${API}/notifications/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          enabled: notifEnabled,
          frequency: notifFrequency,
          fields: notifFields,
          countries: notifCountries,
          keywords
        })
      });

      if (response.ok) {
        toast.success("Notification preferences saved!");
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await fetch(`${API}/notifications/test`, {
        method: "POST",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Test email sent! Check your inbox.");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to send test email");
      }
    } catch (error) {
      toast.error("Failed to send test email. SendGrid may not be configured.");
    } finally {
      setSendingTest(false);
    }
  };

  const submitWebsite = async (e) => {
    e.preventDefault();
    
    if (!websiteUrl || !websiteName) {
      toast.error("Please provide URL and name");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/websites/submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          url: websiteUrl,
          name: websiteName,
          description: websiteDescription
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setWebsiteUrl("");
        setWebsiteName("");
        setWebsiteDescription("");
        
        // Refresh submissions
        const subsRes = await fetch(`${API}/websites/submissions`, { credentials: "include" });
        if (subsRes.ok) {
          setUserSubmissions(await subsRes.json());
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to submit website");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      approved: "bg-green-500/10 text-green-400 border-green-500/20",
      rejected: "bg-red-500/10 text-red-400 border-red-500/20"
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-48 mb-8" />
          <div className="space-y-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="settings-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl md:text-4xl font-medium text-white mb-2">
          Settings
        </h1>
        <p className="text-gray-400 mb-8">
          Manage your notification preferences and suggest job sources
        </p>

        <Tabs defaultValue="notifications" className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-white/10 p-1 mb-8">
            <TabsTrigger 
              value="notifications" 
              className="data-[state=active]:bg-[#800020] data-[state=active]:text-white rounded-sm"
              data-testid="tab-notifications"
            >
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger
              value="websites"
              className="data-[state=active]:bg-[#800020] data-[state=active]:text-white rounded-sm"
              data-testid="tab-websites"
            >
              <Globe className="w-4 h-4 mr-2" />
              Job Sources
            </TabsTrigger>
          </TabsList>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Enable/Disable */}
              <div className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {notifEnabled ? (
                      <Bell className="w-5 h-5 text-[#D4AF37]" />
                    ) : (
                      <BellOff className="w-5 h-5 text-gray-500" />
                    )}
                    <div>
                      <h3 className="font-semibold text-white">Email Notifications</h3>
                      <p className="text-sm text-gray-500">Get notified about new positions</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifEnabled}
                    onCheckedChange={setNotifEnabled}
                    data-testid="notif-enable-switch"
                  />
                </div>

                {notifEnabled && (
                  <div className="space-y-6 pt-6 border-t border-white/10">
                    {/* Frequency */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Notification Frequency</Label>
                      <Select value={notifFrequency} onValueChange={setNotifFrequency}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="notif-frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          <SelectItem value="instant">
                            <span className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Instant - As soon as new jobs are posted
                            </span>
                          </SelectItem>
                          <SelectItem value="daily">
                            <span className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              Daily Digest - Once per day
                            </span>
                          </SelectItem>
                          <SelectItem value="weekly">
                            <span className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              Weekly Summary - Once per week
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Fields Filter */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Research Fields (leave empty for all)</Label>
                      <div className="flex flex-wrap gap-2">
                        {filterOptions.fields.map(field => (
                          <button
                            key={field}
                            onClick={() => {
                              if (notifFields.includes(field)) {
                                setNotifFields(notifFields.filter(f => f !== field));
                              } else {
                                setNotifFields([...notifFields, field]);
                              }
                            }}
                            className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${
                              notifFields.includes(field)
                                ? "bg-[#800020]/30 border-[#800020] text-white"
                                : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"
                            }`}
                          >
                            {field}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Countries Filter */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Countries (leave empty for all)</Label>
                      <div className="flex flex-wrap gap-2">
                        {filterOptions.countries.map(country => (
                          <button
                            key={country}
                            onClick={() => {
                              if (notifCountries.includes(country)) {
                                setNotifCountries(notifCountries.filter(c => c !== country));
                              } else {
                                setNotifCountries([...notifCountries, country]);
                              }
                            }}
                            className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${
                              notifCountries.includes(country)
                                ? "bg-[#800020]/30 border-[#800020] text-white"
                                : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"
                            }`}
                          >
                            {country}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Keywords */}
                    <div>
                      <Label className="text-gray-300 mb-2 block">Keywords (comma-separated)</Label>
                      <Input
                        placeholder="machine learning, quantum computing, genomics..."
                        value={notifKeywords}
                        onChange={(e) => setNotifKeywords(e.target.value)}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="notif-keywords-input"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Get notified when job titles or descriptions contain these keywords
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Save & Test Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={saveNotificationPreferences}
                  disabled={saving}
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold rounded-sm"
                  data-testid="save-notif-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Preferences
                    </>
                  )}
                </Button>
                
                {notifEnabled && (
                  <Button
                    onClick={sendTestEmail}
                    disabled={sendingTest}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 rounded-sm"
                    data-testid="test-email-btn"
                  >
                    {sendingTest ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Test Email
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-sm bg-[#800020]/10 border border-[#800020]/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-400">
                  Emails are sent to <span className="text-white">{user?.email}</span>. 
                  Make sure to check your spam folder if you don't receive notifications.
                </p>
              </div>
            </motion.div>
          </TabsContent>

          {/* Websites Tab */}
          <TabsContent value="websites">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Submit New Website */}
              <div className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#D4AF37]" />
                  Suggest a Job Source
                </h3>
                <p className="text-sm text-gray-400 mb-6">
                  Know a website that posts postdoc positions? Submit it for review and we'll add it to our sources.
                </p>
                
                <form onSubmit={submitWebsite} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300 mb-2 block">Website URL *</Label>
                      <Input
                        type="url"
                        placeholder="https://example.com/jobs"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="website-url-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block">Website Name *</Label>
                      <Input
                        placeholder="Nature Careers"
                        value={websiteName}
                        onChange={(e) => setWebsiteName(e.target.value)}
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                        data-testid="website-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300 mb-2 block">Description (optional)</Label>
                    <Textarea
                      placeholder="Brief description of the website and what types of positions it lists..."
                      value={websiteDescription}
                      onChange={(e) => setWebsiteDescription(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[80px]"
                      data-testid="website-desc-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-bold rounded-sm"
                    data-testid="submit-website-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Globe className="w-4 h-4 mr-2" />
                        Submit for Review
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* User's Submissions */}
              {userSubmissions.length > 0 && (
                <div className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10">
                  <h3 className="font-semibold text-white mb-4">Your Submissions</h3>
                  <div className="space-y-3">
                    {userSubmissions.map((sub) => (
                      <div 
                        key={sub.submission_id}
                        className="flex items-center justify-between p-3 rounded-sm bg-white/5 border border-white/5"
                      >
                        <div>
                          <p className="text-white font-medium">{sub.name}</p>
                          <a 
                            href={sub.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-gray-500 hover:text-[#D4AF37] flex items-center gap-1"
                          >
                            {sub.url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <Badge className={getStatusBadge(sub.status)}>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approved Sources */}
              <div className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" />
                  Approved Job Sources ({approvedSources.length})
                </h3>
                {approvedSources.length === 0 ? (
                  <p className="text-gray-500 text-sm">No approved sources yet. Be the first to suggest one!</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {approvedSources.map((source) => (
                      <a
                        key={source.source_id}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 rounded-sm bg-white/5 border border-white/5 hover:border-[#D4AF37]/50 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-white font-medium group-hover:text-[#D4AF37]">{source.name}</p>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-[#D4AF37]" />
                        </div>
                        {source.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{source.description}</p>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
